import { useWatch } from 'react-hook-form';
import React from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormLabel,
    LinearProgress,
    Link,
    Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from 'api/api';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';
import dayjs from 'dayjs';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { useEntityIssues, EntityIssuesType } from 'hooks/useEntityIssues';
import InconsistenciesWrapper from './InconsistenciesWrapper';
import { formatHoldText, KgHeldReason, KgHoldGating } from './kgHold';
import { setIssuesTarget, setTargetQuestionId, show } from 'store/conciergeSlice';
import { useDispatch } from 'react-redux';
import IconMi from 'components/common/icon/IconMi';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { RawFormElementProps } from 'components/common/form';
import useEverTrue from 'hooks/useEverTrue';
import { Stack } from '@mui/system';
import { ConfirmationPopupWrapper, useConfirmationPopup } from 'components/common/popup/ConfirmationPopup';

type KgStatus = 'not_in_graph' | 'profiling' | 'review_required' | 'available' | 'removed';
const KG_STATUSES: KgStatus[] = ['not_in_graph', 'profiling', 'review_required', 'available', 'removed'];
const isKgStatus = (v: unknown): v is KgStatus => KG_STATUSES.includes(v as KgStatus);
type KgAction = 'add' | 'reprofile' | 'remove' | 'make_available';
// 'element' = external report surface: issues fetched via the element-keyed API (MI-29708)
type KgEntityType = 'dataset' | 'domain' | 'element';

export interface ConciergeKgControlProps extends FormControlProps {
    entityType?: KgEntityType;
    entityId: number;
    nameField: string;
    profiledTimeField: string;
    actionUrl: string;
    // null until the entity has been admitted to the graph and has a node to link to
    knowledgeGraphUrl: string | null;
    /** Translation bundle; the report editor passes a model-worded bundle (MI-29708). */
    translationNS?: string;
    /** Dataset-only Concierge fields (description/instructions) in the available state. */
    showConciergeFields?: boolean;
}

interface TransitionResponse {
    concierge_kg_status: KgStatus;
    concierge_kg_profiled_time: string | null;
    concierge_kg_active_run_uid?: string | null;
    outcome?: string;
    held_reason?: KgHeldReason;
    gating?: KgHoldGating;
}

type LastRunStatus = 'failed' | 'cancelled' | 'partial';
const isLastRunStatus = (v: unknown): v is LastRunStatus => ['failed', 'cancelled', 'partial'].includes(v as string);

// Status poll payload (MI-30376) served by the dataset and domain hosts; the report surface does not poll.
interface StatusResponse {
    concierge_kg_status: KgStatus;
    concierge_kg_profiled_time: string | null;
    concierge_kg_active_run_uid: string | null;
    concierge_kg_last_run_status: LastRunStatus | null;
    concierge_kg_last_run_error: string | null;
    concierge_kg_last_run_time: string | null;
    concierge_kg_measure_count?: number | null;
    concierge_kg_dimension_count?: number | null;
    concierge_kg_open_item_count?: number | null;
    concierge_kg_publish_blocked?: boolean;
    concierge_kg_can_publish?: boolean;
    concierge_kg_node_url?: string | null;
    concierge_kg_run_refusal?: string | null;
}

type PublishHold = { reason: KgHeldReason; gating?: KgHoldGating };

const TRANSLATION_NS: Record<KgEntityType, string> = {
    dataset: 'components/common/concierge-kg-control',
    domain: 'components/common/concierge-kg-control-domain',
    element: 'components/common/concierge-kg-control-report',
};

export default function ConciergeKgControl({ controlProps }: FormElementControlPropsType<ConciergeKgControlProps>) {
    const entityType: KgEntityType = controlProps.entityType ?? 'dataset';
    const isDomain = entityType === 'domain';
    const isDataset = entityType === 'dataset';
    const { t } = useBundleTranslation([controlProps.translationNS ?? TRANSLATION_NS[entityType]]);
    const dispatch = useDispatch();
    const showConciergeFields = controlProps.showConciergeFields ?? true;
    // Dataset only: why the server will not start a run (form load, status poll, save response). useWatch, not a
    // render-time watch: a save's updated fields do not re-render this control otherwise.
    const runRefusal: string =
        useWatch({ control: controlProps.form.hookFormControl, name: 'concierge_kg_run_refusal' }) ?? '';
    const isActionDisabled = (action: KgAction) => '' !== runRefusal && ('add' === action || 'reprofile' === action);
    // Domain only: the Info-tab routing toggle. Publishing a disabled domain is allowed (MI-30870), so the
    // available state says Concierge will not use it instead of "Available in Concierge".
    const enabledInd: string | undefined = useWatch({
        control: controlProps.form.hookFormControl,
        name: 'enabled_ind',
    });
    const isDomainDisabled = isDomain && enabledInd === 'N';

    const rawStatus = controlProps.form.hookFormWatch(controlProps.name) ?? controlProps.value;
    const status: KgStatus = isKgStatus(rawStatus) ? rawStatus : 'not_in_graph';
    const entityName = controlProps.form.hookFormWatch(controlProps.nameField) as string;
    const profiledTime = controlProps.form.hookFormWatch(controlProps.profiledTimeField) as string | null;
    // Hidden fields declared by the dataset and domain forms; undefined on the report surface.
    // A Re-profile leaves the persisted status at its endpoint and shows only as the active run uid (MI-30717):
    // the tab presents any live run as profiling until it ends (MI-30840). useWatch for the same reason as
    // runRefusal: the poll that ends a re-profile changes this field alone.
    const activeRunUid: string =
        useWatch({ control: controlProps.form.hookFormControl, name: 'concierge_kg_active_run_uid' }) ?? '';
    const isRunInFlight = status === 'profiling' || Boolean(activeRunUid);
    const viewStatus: KgStatus = isRunInFlight ? 'profiling' : status;
    const rawLastRunStatus = controlProps.form.hookFormWatch('concierge_kg_last_run_status');
    const lastRunStatus = isLastRunStatus(rawLastRunStatus) ? rawLastRunStatus : null;
    const lastRunError: string = controlProps.form.hookFormWatch('concierge_kg_last_run_error') ?? '';
    // Publish readiness (MI-30308), derived server-side for dataset and domain hosts. The count is
    // null when the server could not settle it (no live root, failed graph read) — never treated as
    // zero. can_publish absent (report surface, older forms) keeps the verb visible as before.
    const rawOpenItemCount = controlProps.form.hookFormWatch('concierge_kg_open_item_count');
    const openItemCount: number | null = typeof rawOpenItemCount === 'number' ? rawOpenItemCount : null;
    // The publish gate still parks the host: never "Ready to Publish", even with no open items listed.
    const publishBlocked: boolean = controlProps.form.hookFormWatch('concierge_kg_publish_blocked') === true;
    const canPublish: boolean = controlProps.form.hookFormWatch('concierge_kg_can_publish') !== false;
    // can_publish is canManageKnowledgeGraph(), the same gate the KG editor routes use
    const canManageKg = canPublish;
    // Profile summary counts from the dataset and domain hosts; the report surface sends none.
    const rawMeasureCount = controlProps.form.hookFormWatch('concierge_kg_measure_count');
    const rawDimensionCount = controlProps.form.hookFormWatch('concierge_kg_dimension_count');
    const profileCounts =
        typeof rawMeasureCount === 'number' && typeof rawDimensionCount === 'number'
            ? { measures: rawMeasureCount, dimensions: rawDimensionCount }
            : null;

    const issues = useEntityIssues(controlProps.entityId, entityType as EntityIssuesType, true);
    // Once items have shown, the wrapper owns the section (incl. its resolved/all-clear states) —
    // never fall back to the empty-state placeholder mid-session
    const hasOpenItems = useEverTrue(
        Boolean(
            issues.context &&
            (issues.context.inconsistencies.length > 0 || issues.context.clarifyingQuestions.length > 0),
        ),
    );

    const tParams = { dataset: entityName, name: entityName };
    const toastAvailable = () =>
        toast(
            isDomainDisabled ? 'warning' : 'success',
            t(isDomainDisabled ? 'toast_available_domain_disabled' : 'toast_available', tParams),
        );

    const queryClient = useQueryClient();
    const [hold, setHold] = React.useState<PublishHold | null>(null);
    const holdIssuesRef = React.useRef(issues.context);
    const statusQueryKey = ['conciergeKgStatus', entityType, controlProps.entityId];
    const mountedAtRef = React.useRef(Date.now());

    const mutation = useMutation({
        mutationFn: (action: KgAction) =>
            instance.post<{ status: string; data: TransitionResponse }>(`${controlProps.actionUrl}/${action}`),
        onSuccess: (response) => {
            // A refused transition answers 200 with status ERROR: surface the reason, touch nothing.
            if (response.data.status !== 'OK') {
                toast('error', (response.data.data as { message?: string } | undefined)?.message ?? t('action_failed'));
                return;
            }
            const data = response.data.data;
            const { concierge_kg_status, concierge_kg_profiled_time } = data;
            // Hold first, status second: the review state must never render without its reason
            if (data.outcome === 'held_review') {
                holdIssuesRef.current = issues.context;
                setHold({ reason: data.held_reason ?? 'open_items', gating: data.gating });
                // The open items the hold names are the ones the list below should show now
                void queryClient.invalidateQueries({ queryKey: ['entityIssues', entityType, controlProps.entityId] });
            } else {
                setHold(null);
            }
            controlProps.form.hookFormSetValue(controlProps.name, concierge_kg_status, { shouldDirty: false });
            controlProps.form.hookFormSetValue(controlProps.profiledTimeField, concierge_kg_profiled_time, {
                shouldDirty: false,
            });
            if (concierge_kg_status === 'available') {
                toastAvailable();
            }
            // A fresh run starting hides the previous run's failure/cancellation immediately.
            if (concierge_kg_status === 'profiling') {
                controlProps.form.hookFormSetValue(
                    'concierge_kg_active_run_uid',
                    data.concierge_kg_active_run_uid ?? null,
                    { shouldDirty: false },
                );
                controlProps.form.hookFormSetValue('concierge_kg_last_run_status', null, { shouldDirty: false });
                controlProps.form.hookFormSetValue('concierge_kg_last_run_error', null, { shouldDirty: false });
                controlProps.form.hookFormSetValue('concierge_kg_last_run_time', null, { shouldDirty: false });
                queryClient.removeQueries({ queryKey: statusQueryKey });
            }
        },
        onError: () => {
            toast('error', t('action_failed'));
        },
    });

    const isPending = mutation.isPending;
    const handleAction = (action: KgAction) => mutation.mutate(action);
    const isMakingAvailable = isPending && mutation.variables === 'make_available';
    const removeConfirmPopup = useConfirmationPopup(() => handleAction('remove'));

    // Re-read the host's status while a run is in flight (MI-30376), fast enough to feel live.
    const shouldPoll = (isDataset || isDomain) && (isRunInFlight || status === 'review_required');
    const statusQuery = useQuery<StatusResponse>({
        queryKey: statusQueryKey,
        queryFn: async () =>
            (await instance.get<{ status: string; data: StatusResponse }>(controlProps.actionUrl)).data.data,
        enabled: shouldPoll,
        refetchInterval: isRunInFlight ? 5000 : 30000,
        refetchIntervalInBackground: true,
    });
    React.useEffect(() => {
        const data = statusQuery.data;
        // A cache hit from before this mount (or from the run that just ended — removeQueries
        // above covers the same-mount case) is not a fresh read; skip it rather than apply it.
        if (!data || statusQuery.dataUpdatedAt < mountedAtRef.current) {
            return;
        }
        // 'viewStatus' here is the pre-poll value from this render, i.e. what the pill showed before
        // this tick — the right side of the comparison catches both a run finishing (profiling ->
        // available) and the last clarifying question closing it out (review_required -> available).
        // A Re-profile's poll reads 'available' while its run is still live: not a finish. Nor is a
        // cancelled one, which ends with the host where it was.
        if (
            (viewStatus === 'profiling' || viewStatus === 'review_required') &&
            data.concierge_kg_status === 'available' &&
            !data.concierge_kg_active_run_uid &&
            data.concierge_kg_last_run_status !== 'cancelled'
        ) {
            toastAvailable();
        }
        const fieldUpdates: Record<string, string | number | boolean | null> = {
            [controlProps.name]: data.concierge_kg_status,
            [controlProps.profiledTimeField]: data.concierge_kg_profiled_time,
            concierge_kg_active_run_uid: data.concierge_kg_active_run_uid,
            concierge_kg_last_run_status: data.concierge_kg_last_run_status,
            concierge_kg_last_run_error: data.concierge_kg_last_run_error,
            concierge_kg_last_run_time: data.concierge_kg_last_run_time,
            concierge_kg_measure_count: data.concierge_kg_measure_count ?? null,
            concierge_kg_dimension_count: data.concierge_kg_dimension_count ?? null,
            concierge_kg_open_item_count: data.concierge_kg_open_item_count ?? null,
            concierge_kg_publish_blocked: data.concierge_kg_publish_blocked ?? false,
            concierge_kg_can_publish: data.concierge_kg_can_publish ?? true,
            concierge_kg_run_refusal: data.concierge_kg_run_refusal ?? null,
        };
        Object.entries(fieldUpdates).forEach(([field, value]) => {
            controlProps.form.hookFormSetValue(field, value, { shouldDirty: false });
        });
    }, [statusQuery.data, statusQuery.dataUpdatedAt]);

    const pillConfig: Record<
        KgStatus,
        { label: string; color: 'default' | 'primary' | 'warning' | 'success' | 'error'; icon: React.ReactNode }
    > = {
        not_in_graph: {
            label: t('pill_not_in_graph', tParams),
            color: 'default',
            icon: <IconMi icon="catalog" />,
        },
        profiling: {
            label: t('pill_profiling', tParams),
            color: 'primary',
            icon: <CircularProgress size={12} color="inherit" />,
        },
        review_required: {
            label: t('pill_review_required', tParams),
            color: 'warning',
            icon: <IconMi icon="alert" />,
        },
        available: {
            label: t(isDomainDisabled ? 'pill_available_domain_disabled' : 'pill_available', tParams),
            color: isDomainDisabled ? 'warning' : 'success',
            icon: <IconMi icon={isDomainDisabled ? 'alert' : 'check'} />,
        },
        removed: {
            label: t('pill_removed', tParams),
            color: 'error',
            icon: <IconMi icon="catalog" />,
        },
    };

    const profiledDate = profiledTime ? dayjs(profiledTime).format('MMM D, YYYY') : null;

    const handleOpenConcierge = (questionId?: string) => {
        dispatch(setIssuesTarget({ type: entityType, id: controlProps.entityId }));
        dispatch(setTargetQuestionId(questionId ?? null));
        dispatch(show());
    };

    const holdText = (h: PublishHold) => formatHoldText(h.reason, h.gating, t, tParams);

    // An open-items hold describes the issues list below it; once a refreshed list (never the one
    // cached at park time) shows nothing open, the hold has nothing left to say
    React.useEffect(() => {
        const context = issues.context;
        if (!hold || hold.reason !== 'open_items' || !context || context === holdIssuesRef.current) {
            return;
        }
        const nothingOpen =
            context.inconsistencies.length === 0 && context.clarifyingQuestions.every((q) => q.status !== 'Unanswered');
        if (nothingOpen) {
            setHold(null);
        }
    }, [hold, issues.context]);

    // Nothing left to review at review_required: the host can be published now (MI-30308). A hold
    // from a just-parked publish wins — the review view must show its reason, not offer the verb again.
    const isReady = viewStatus === 'review_required' && openItemCount === 0 && !publishBlocked && !hold;

    // The node appears mid-run, after the form loaded its link (MI-30715).
    const knowledgeGraphUrl = statusQuery.data?.concierge_kg_node_url ?? controlProps.knowledgeGraphUrl;

    // Filled where the link is the leading action (ready to publish, available), text elsewhere
    const viewInKgLink = canManageKg && (
        <Tooltip title={knowledgeGraphUrl ? undefined : t('view_in_kg_no_node')}>
            <Box sx={{ display: 'inline-flex' }}>
                <Button
                    variant={isReady || viewStatus === 'available' ? 'contained' : 'text'}
                    component={'a'}
                    href={knowledgeGraphUrl ?? '#'}
                    target={'_blank'}
                    disabled={!knowledgeGraphUrl}
                    endIcon={<IconMi icon="external-link" />}
                    data-test="concierge_kg_link_view"
                >
                    {t('btn_view_in_kg')}
                </Button>
            </Box>
        </Tooltip>
    );

    // A disabled button fires no hover events, so the tooltip sits on a wrapper.
    const withRefusalTooltip = (action: KgAction, button: React.ReactElement) =>
        isActionDisabled(action) ? (
            <Tooltip title={runRefusal}>
                <Box component="span" data-test={`concierge_kg_${action}_refusal`} sx={{ display: 'inline-flex' }}>
                    {button}
                </Box>
            </Tooltip>
        ) : (
            button
        );

    const reprofileLink = withRefusalTooltip(
        'reprofile',
        <Button
            startIcon={<IconMi icon="refresh-cw" />}
            variant={'outlined'}
            disabled={isActionDisabled('reprofile')}
            data-test="concierge_kg_reprofile"
            onClick={() => {
                if (!isActionDisabled('reprofile')) {
                    handleAction('reprofile');
                }
            }}
        >
            {t('btn_reprofile')}
        </Button>,
    );

    // The routing consequence of the Info-tab toggle lives above the section (ConciergeDomainPreview)
    const domainStateLine = isReady
        ? canPublish
            ? 'state_line_ready_to_publish'
            : 'state_line_ready_to_publish_no_permission'
        : isDomainDisabled && viewStatus === 'available'
          ? 'state_line_available_domain_disabled'
          : `state_line_${viewStatus}`;
    const domainHelper = isDomain ? t(domainStateLine) : null;

    const pill = isReady
        ? { label: t('pill_ready_to_publish', tParams), color: 'primary' as const, icon: <IconMi icon="check" /> }
        : pillConfig[viewStatus];

    const conciergeFields = !isDomain && showConciergeFields && (
        <>
            <FormControl>
                <FormLabel>{t('description_for_concierge')}</FormLabel>
                <TemplateFormComponent
                    elementProps={{ form: controlProps.form } as RawFormElementProps}
                    component={'FormTextarea'}
                    name={'description_for_concierge'}
                    props={{ rows: 4 }}
                />
            </FormControl>
            <FormControl>
                <FormLabel>{t('concierge_instructions')}</FormLabel>
                <TemplateFormComponent
                    elementProps={{ form: controlProps.form } as RawFormElementProps}
                    component={'FormTextarea'}
                    name={'concierge_instructions'}
                    props={{ rows: 4 }}
                />
            </FormControl>
        </>
    );

    const makeAvailableButton = canPublish && (
        <Box>
            <Button
                variant="outlined"
                startIcon={isMakingAvailable ? <CircularProgress size={14} color="inherit" /> : <IconMi icon="check" />}
                onClick={() => handleAction('make_available')}
                disabled={isPending}
                data-test="concierge_kg_btn_make_available"
            >
                {t(isMakingAvailable ? 'btn_make_available_pending' : 'btn_make_available')}
            </Button>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
            <LoadingPlaceholder open={isPending && !isMakingAvailable} />
            <Stack spacing={1}>
                {viewStatus === 'review_required' && !isReady ? (
                    <Alert icon={<IconMi icon="alert" />} severity={'warning'} variant={'accent'}>
                        {openItemCount
                            ? t('pill_review_required_count', { ...tParams, items: openItemCount })
                            : t('pill_review_required', tParams)}
                    </Alert>
                ) : (
                    <Box>
                        <Chip label={pill.label} color={pill.color} icon={pill.icon as React.ReactElement} />
                    </Box>
                )}

                {!activeRunUid && lastRunStatus && (
                    <Alert
                        icon={<IconMi icon="alert" />}
                        severity={lastRunStatus === 'failed' ? 'error' : 'warning'}
                        variant={'accent'}
                        data-test="concierge_kg_last_run_outcome"
                    >
                        {lastRunStatus === 'failed' && lastRunError
                            ? t('last_run_failed_with_error', { ...tParams, error: lastRunError })
                            : t(`last_run_${lastRunStatus}`, tParams)}
                    </Alert>
                )}

                {domainHelper && (
                    <Box
                        sx={{
                            color: 'text.secondary',
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                        }}
                        data-test="concierge_kg_helper"
                    >
                        {domainHelper}
                    </Box>
                )}

                {viewStatus === 'not_in_graph' && (
                    <>
                        {!isDomain && (
                            <Box
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                                }}
                            >
                                {t('helper_not_in_graph')}
                            </Box>
                        )}
                        <Box sx={{ pt: 1 }}>
                            {withRefusalTooltip(
                                'add',
                                <Button
                                    startIcon={<IconMi icon="new" />}
                                    onClick={() => handleAction('add')}
                                    disabled={isPending || isActionDisabled('add')}
                                    data-test="concierge_kg_btn_add"
                                    variant="outlined"
                                >
                                    {t('btn_add')}
                                </Button>,
                            )}
                        </Box>
                    </>
                )}
            </Stack>

            {viewStatus === 'profiling' && (
                <>
                    <Alert severity={'warning'} variant={'accent'} data-test="concierge_kg_profiling_note">
                        {t(isDataset && status === 'available' ? 'note_reprofiling' : 'note_profiling', tParams)}
                    </Alert>
                    <LinearProgress data-test="concierge_kg_progress_bar" />
                    {status === 'available' && conciergeFields}
                </>
            )}

            {isReady && (
                <>
                    {!isDomain && (
                        <Alert severity={'info'} variant={'accent'} data-test="concierge_kg_ready_note">
                            {t('note_ready_to_publish', tParams)}
                        </Alert>
                    )}
                    {makeAvailableButton}
                    {viewInKgLink}
                    <InconsistenciesWrapper
                        issues={issues}
                        onOpenConcierge={handleOpenConcierge}
                        objectType={entityType === 'element' ? 'report' : entityType}
                    />
                </>
            )}

            {viewStatus === 'review_required' && !isReady && (
                <>
                    <Alert icon={<IconMi icon={'alert'} />} severity={'warning'} variant={'accent'}>
                        {t('warning_review_required', tParams)}
                        {hold && (
                            <Box data-test="concierge_kg_hold" sx={{ mt: 1 }}>
                                {holdText(hold)}
                                {hold.reason === 'open_items' && hold.gating?.open_clarifications && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={() => handleOpenConcierge()}
                                            data-test="concierge_kg_hold_answer_questions"
                                        >
                                            {t('hold_answer_questions_link')}
                                        </Link>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Alert>
                    {viewInKgLink}
                    <InconsistenciesWrapper
                        issues={issues}
                        onOpenConcierge={handleOpenConcierge}
                        objectType={entityType === 'element' ? 'report' : entityType}
                    />

                    {!hasOpenItems && !hold && (
                        <Alert icon={<IconMi icon={'alert'} />} severity={'warning'} variant={'accent'}>
                            <AlertTitle>
                                <Box component={'strong'}>{t('to_resolve_title')}</Box>
                            </AlertTitle>
                            {t('to_resolve_placeholder')}
                        </Alert>
                    )}
                </>
            )}

            {viewStatus === 'available' && (
                <>
                    {profiledDate && (
                        <Box sx={{ color: 'text.secondary' }} data-test="concierge_kg_profile_summary">
                            {profileCounts
                                ? t('profile_summary', {
                                      date: profiledDate,
                                      measures: t('profile_summary_measures', { count: profileCounts.measures }),
                                      dimensions: t('profile_summary_dimensions', { count: profileCounts.dimensions }),
                                  })
                                : t('profile_summary_date_only', { date: profiledDate })}
                        </Box>
                    )}
                    {conciergeFields}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {viewInKgLink}
                        {reprofileLink}

                        <Button
                            startIcon={<IconMi icon="times" />}
                            variant={'outlined'}
                            color="error"
                            data-test="concierge_kg_remove"
                            onClick={() => {
                                removeConfirmPopup.setContent(t('remove_confirm', tParams));
                                removeConfirmPopup.setIsOpen(true);
                            }}
                        >
                            {t('btn_remove')}
                        </Button>
                    </Box>
                    <ConfirmationPopupWrapper confirmationPopup={removeConfirmPopup} />
                </>
            )}

            {viewStatus === 'removed' && (
                <>
                    {!isDomain && (
                        <Alert icon={<IconMi icon="alert" />} severity={'warning'} variant={'accent'}>
                            {t('helper_removed', tParams)}
                        </Alert>
                    )}
                    {makeAvailableButton}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {viewInKgLink}
                        {reprofileLink}
                    </Box>
                </>
            )}
        </Box>
    );
}

export class ConciergeKgControlBuilder extends FormComponentBuilder {
    prepareProps(): ConciergeKgControlProps {
        return {
            ...this.controlProps,
            entityType: this.elementProps.componentProps?.entityType,
            entityId: this.elementProps.componentProps?.entityId,
            nameField: this.elementProps.componentProps?.nameField,
            profiledTimeField: this.elementProps.componentProps?.profiledTimeField,
            actionUrl: this.elementProps.componentProps?.actionUrl,
            knowledgeGraphUrl: this.elementProps.componentProps?.knowledgeGraphUrl ?? null,
            translationNS: this.elementProps.componentProps?.translationNS,
            showConciergeFields: this.elementProps.componentProps?.showConciergeFields,
        };
    }
}
